import { useContext, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@fmc-digital/i18n";
import { Button, cn } from "@fmc-digital/ui";
import { registerStepFormStyles } from "@profile/styles/register-flow.styles";
import { updateProfileFactors } from "@fmc-digital/auth-api-generated/src/api/fmc/unauth/customer/updateProfileFactors";
import { getRegisterErrorMessageFromCode } from "@profile/features/registration/registerErrorMessage";
import { RegisterContext } from "@profile/contexts/RegisterContext";
import { getRegisterFeedbackEntryNavigation } from "@profile/components/registration-wizard/feedback/register-feedback.navigation";
import { RegistrationEvent } from "@profile/features/registration/analytics";
import { useRegistrationProvider } from "@profile/providers/RegistrationProvider";

interface PhoneConsentStepProps {
  setStatusMessage: (msg: string | null) => void;
}

export function PhoneConsentStep({ setStatusMessage }: PhoneConsentStepProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { trackRegistrationEvent } = useRegistrationProvider();
  const { email, otpId, phoneFactorId, setPhoneConsent, userUUID, personalInfo } = useContext(RegisterContext);
  const [selection, setSelection] = useState<"yes" | "no" | null>(null);
  const [smsConsentChecked, setSmsConsentChecked] = useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const [loading, setLoading] = useState(false);

  const consentBodyStyle = {
    color: "var(--Schemes-On-Surface-Variant, #44474C)",
    fontFamily: "Poppins",
    fontSize: "12px",
    fontStyle: "normal" as const,
    fontWeight: 400,
    lineHeight: "16px",
    letterSpacing: "0.4px",
  };

  const consentLinkStyle: CSSProperties = {
    color: "var(--Schemes-Primary, #003049)",
    fontFamily: "Poppins",
    fontSize: "var(--Static-Body-Small-Size, 12px)",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "var(--Static-Body-Small-Line-Height, 16px)",
    letterSpacing: "var(--Static-Body-Small-Tracking, 0.4px)",
    textDecorationLine: "underline",
    textDecorationStyle: "solid",
    textDecorationSkipInk: "auto",
    textDecorationThickness: "auto",
    textUnderlineOffset: "auto",
    textUnderlinePosition: "from-font",
  };

  const consentErrorStyle = {
    color: "var(--Schemes-Error, #B42528)",
    fontFamily: "var(--Font-Family-body, Poppins)",
    fontSize: "var(--Font-Size-xs, 13px)",
    fontStyle: "normal" as const,
    fontWeight: 400,
    lineHeight: "normal",
  };

  const radioOptionStyle = {
    borderRadius: "12px",
    border: "1px solid var(--Freedom-M3-sys-dark-on-surface-variant, #C4C6CC)",
    display: "flex",
    padding: "8px",
    alignItems: "center",
    gap: "7px",
    alignSelf: "stretch",
    width: "100%",
  };

  const radioContainerStyle = {
    position: "relative" as const,
    width: "24px",
    height: "24px",
    flexShrink: 0,
  };

  const radioInputStyle = {
    position: "absolute" as const,
    inset: 0,
    margin: 0,
    opacity: 0,
    cursor: "pointer",
  };

  const radioVisualStyle = {
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none" as const,
  };

  const checkboxContainerStyle = {
    position: "relative" as const,
    display: "flex",
    width: "36px",
    height: "36px",
    padding: "4px",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
    aspectRatio: "1 / 1",
    flexShrink: 0,
  };

  const checkboxInputStyle = {
    position: "absolute" as const,
    inset: 0,
    margin: 0,
    opacity: 0,
    cursor: "pointer",
  };

  const checkboxVisualStyle = {
    width: "24px",
    height: "24px",
    borderRadius: "4px",
    border: "1px solid var(--Freedom-M3-sys-dark-on-surface-variant, #C4C6CC)",
    background: smsConsentChecked ? "#003049" : "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none" as const,
  };

  const consentErrorAlignStyle = {
    marginLeft: "44px",
  };

  const noSelectionConsentText = "By selecting NO, every login will require you to access your email for MFA verification";

  const translate = (key: string) => t(key as never) as string;

  const handleNext = async () => {
    if (!selection || !smsConsentChecked) {
      setShowConsentError(Boolean(selection) && !smsConsentChecked);
      return;
    }

    setStatusMessage(null);
    setShowConsentError(false);

    if (!otpId || !phoneFactorId) {
      setStatusMessage(translate("auth.error.generic"));
      return;
    }

    setLoading(true);
    try {
      await updateProfileFactors({
        id: otpId,
        factorId: phoneFactorId,
        action: selection === "yes" ? "ADD" : "SKIP",
      });

      try {
        trackRegistrationEvent(RegistrationEvent.PHONE_UPDATED, {
          description: "When users account successfully updated with the mobile Phone for new registration flow",
        });
      } catch {
        // Analytics must not block flow
      }

      setPhoneConsent(selection === "yes");
      const { route, state } = getRegisterFeedbackEntryNavigation({
        email,
        userUUID,
        loanNumber: personalInfo.loanNumber,
      });
      navigate(route, state ? { state } : undefined);
    } catch (error) {
      setStatusMessage(
        getRegisterErrorMessageFromCode(
          error,
          translate,
          i18n.exists.bind(i18n),
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cn(registerStepFormStyles(), "md:max-w-md md:mx-auto")}>
      <div role="radiogroup" aria-label="MFA mobile consent" className="flex flex-col gap-4 mt-2">
        <label className="text-base text-[#1B1C1D]" style={radioOptionStyle}>
          <span style={radioContainerStyle}>
            <input
              type="radio"
              name="phone-consent"
              value="yes"
              checked={selection === "yes"}
              style={radioInputStyle}
              onChange={() => {
                setSelection("yes");
                setSmsConsentChecked(false);
                setStatusMessage(null);
                setShowConsentError(true);
                try {
                  trackRegistrationEvent(RegistrationEvent.PHONE_MFA_CONSENT_OPTED);
                } catch {
                  // Analytics must not block interaction
                }
              }}
            />
            <span style={radioVisualStyle}>
              {selection === "yes" ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 17C13.3833 17 14.5625 16.5125 15.5375 15.5375C16.5125 14.5625 17 13.3833 17 12C17 10.6167 16.5125 9.4375 15.5375 8.4625C14.5625 7.4875 13.3833 7 12 7C10.6167 7 9.4375 7.4875 8.4625 8.4625C7.4875 9.4375 7 10.6167 7 12C7 13.3833 7.4875 14.5625 8.4625 15.5375C9.4375 16.5125 10.6167 17 12 17ZM12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z" fill="#003049"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z" fill="#44474C"/>
                </svg>
              )}
            </span>
          </span>{" "}
          Yes
        </label>
        <label className="text-base text-[#1B1C1D]" style={radioOptionStyle}>
          <span style={radioContainerStyle}>
            <input
              type="radio"
              name="phone-consent"
              value="no"
              checked={selection === "no"}
              style={radioInputStyle}
              onChange={() => {
                setSelection("no");
                setSmsConsentChecked(false);
                setStatusMessage(null);
                setShowConsentError(true);
                try {
                  trackRegistrationEvent(RegistrationEvent.PHONE_MFA_CONSENT_OPTED_OUT);
                } catch {
                  // Analytics must not block interaction
                }
              }}
            />
            <span style={radioVisualStyle}>
              {selection === "no" ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 17C13.3833 17 14.5625 16.5125 15.5375 15.5375C16.5125 14.5625 17 13.3833 17 12C17 10.6167 16.5125 9.4375 15.5375 8.4625C14.5625 7.4875 13.3833 7 12 7C10.6167 7 9.4375 7.4875 8.4625 8.4625C7.4875 9.4375 7 10.6167 7 12C7 13.3833 7.4875 14.5625 8.4625 15.5375C9.4375 16.5125 10.6167 17 12 17ZM12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z" fill="#003049"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z" fill="#44474C"/>
                </svg>
              )}
            </span>
          </span>{" "}
          No
        </label>
      </div>
      {selection && (
        <div className="flex flex-col gap-1">
          <label className="flex items-start gap-2">
            <span style={checkboxContainerStyle}>
              <input
                type="checkbox"
                aria-label="SMS MFA consent"
                checked={smsConsentChecked}
                style={checkboxInputStyle}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setSmsConsentChecked(checked);
                  setShowConsentError(Boolean(selection) && !checked);
                  if (checked) {
                    try {
                      trackRegistrationEvent(RegistrationEvent.PHONE_MFA_CONSENT_CONFIRMED);
                    } catch {
                      // Analytics must not block interaction
                    }
                  }
                }}
              />
              <span style={checkboxVisualStyle}>
                {smsConsentChecked && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M10 16.4L6 12.4L7.4 11L10 13.6L16.6 7L18 8.4L10 16.4Z" fill="white"/>
                  </svg>
                )}
              </span>
            </span>
            <span style={consentBodyStyle}>
              {selection === "no" ? (
                noSelectionConsentText
              ) : (
                <>
                  I consent to receive MFA SMS messages from Freedom Mortgage and have read and agree to the{" "}
                  <a
                    href="https://www.freedommortgage.com/disclosures/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={consentLinkStyle}
                  >
                    Terms & Conditions
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://www.freedommortgage.com/disclosures/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={consentLinkStyle}
                  >
                    Privacy Policy
                  </a>{" "}
                  . Message and data rates may apply. One message sent per request.
                </>
              )}
            </span>
          </label>
          {showConsentError && !smsConsentChecked && (
            <span style={{ ...consentErrorStyle, ...consentErrorAlignStyle }}>*Please check this box to continue.</span>
          )}
        </div>
      )}
      <div className="flex flex-col gap-2 mt-4">
        <Button variant="filled" fullWidth onClick={handleNext} disabled={!selection || !smsConsentChecked || loading}>
          {loading ? "Loading..." : "Next"}
        </Button>
      </div>
    </div>
  );
}
